// assets/ugc-video-carousel.js

class UgcVideoCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.ugc-carousel__track');
    this.slides = this.querySelectorAll('.ugc-carousel__slide');
    this.videos = this.querySelectorAll('video');
    this.prevBtn = this.querySelector('.ugc-carousel__btn--prev');
    this.nextBtn = this.querySelector('.ugc-carousel__btn--next');
    this._abortController = new AbortController();
    this.playMode = this.dataset.playMode || 'autoplay';

    this.initArrows();

    if (this.playMode === 'click') {
      this.initClickToPlay();
    } else {
      this.initAutoplay();
    }

    // Defer layout-dependent checks until after first paint
    requestAnimationFrame(() => {
      this.checkOverflow();
      this.updateArrowStates();
    });

    // Re-check when element resizes (fonts load, images load, etc.)
    this._resizeObserver = new ResizeObserver(() => {
      this.checkOverflow();
      this.updateArrowStates();
    });
    this._resizeObserver.observe(this.track);
  }

  initArrows() {
    const signal = this._abortController.signal;
    this.prevBtn?.addEventListener('click', () => this.shiftTrack(-1), { signal });
    this.nextBtn?.addEventListener('click', () => this.shiftTrack(1), { signal });
    this.track?.addEventListener('scroll', () => this.updateArrowStates(), { signal, passive: true });
  }

  shiftTrack(direction) {
    const slide = this.slides[0];
    if (!slide) return;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 12;
    const slideWidth = slide.offsetWidth + gap;
    this.track.scrollBy({ left: direction * slideWidth, behavior: 'smooth' });
  }

  updateArrowStates() {
    if (!this.track) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const atStart = scrollLeft <= 2;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
    atStart ? this.prevBtn?.setAttribute('disabled', '') : this.prevBtn?.removeAttribute('disabled');
    atEnd ? this.nextBtn?.setAttribute('disabled', '') : this.nextBtn?.removeAttribute('disabled');
  }

  checkOverflow() {
    if (!this.track) return;
    const hasOverflow = this.track.scrollWidth > this.track.clientWidth;
    this.classList.toggle('ugc-carousel__wrapper--has-overflow', hasOverflow);
    this.track.classList.toggle('ugc-carousel__track--centered', !hasOverflow);
  }

  initAutoplay() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const video = e.target.querySelector('video');
          if (!video) return;
          if (e.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  initClickToPlay() {
    const signal = this._abortController.signal;

    this.slides.forEach((slide) => {
      const video = slide.querySelector('video');
      const playBtn = slide.querySelector('.ugc-carousel__play-btn');
      if (!video || !playBtn) return;

      playBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.pauseAll();
        video.play().then(() => {
          slide.classList.add('ugc-carousel__slide--playing');
        }).catch((err) => { console.error('UGC play failed:', err); });
      }, { signal });

      video.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        video.pause();
        // Force mobile browsers to render the paused frame instead of going black
        requestAnimationFrame(() => {
          video.currentTime = video.currentTime;
        });
        slide.classList.remove('ugc-carousel__slide--playing');
      }, { signal });

      video.addEventListener('ended', () => {
        slide.classList.remove('ugc-carousel__slide--playing');
      }, { signal });
    });
  }

  pauseAll() {
    this.slides.forEach((slide) => {
      const video = slide.querySelector('video');
      if (video) video.pause();
      slide.classList.remove('ugc-carousel__slide--playing');
    });
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this.observer?.disconnect();
    this._resizeObserver?.disconnect();
  }
}

customElements.define('ugc-video-carousel', UgcVideoCarousel);
